import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  InputSelector,
  Option,
  useInputField,
} from '@edreamsodigeo/prisma-design-system';
import { FormFieldWrapper } from './FormFieldWrapper';

const useInputSelector = ({
  disabled,
  validate,
  id,
  value: initialValue,
  onBlur,
  showErrors,
}) => {
  const [wasFocused, setWasFocused] = useState(false);
  const [value, setValue] = useState(initialValue);

  const { inputProps, ...props } = useInputField({
    value,
    onClick: () => setWasFocused(true),
    onChange: (event) => {
      setValue(event.currentTarget.value);
    },
    onReset: () => setValue(''),
    disabled,
    type: 'select',
    id,
    validate: (value) => {
      if (validate(value)) return true;
      if (showErrors) return false;
      return !wasFocused;
    },
  });

  const onBlurProp = inputProps.onBlur;
  inputProps.onBlur = () => {
    onBlurProp();
    onBlur(value);
  };

  return { inputProps, ...props };
};

export const FormSelector = ({
  id,
  label,
  values,
  onBlur,
  validate,
  disabled,
  showErrors,
  defaultValue,
  value,
}) => {
  const { labelProps, fieldProps, inputProps, validationProps } =
    useInputSelector({
      disabled,
      validate,
      id,
      value: value || defaultValue,
      onBlur,
      showErrors,
    });

  // to update the model with the initial value
  useEffect(() => {
    if (defaultValue && !value) {
      onBlur(defaultValue);
    }
  }, [value, defaultValue, onBlur]);

  return (
    <FormFieldWrapper
      label={label}
      fieldProps={fieldProps}
      labelProps={labelProps}
      validationProps={validationProps}
    >
      <InputSelector data-testid={`form-field-${id}`} {...inputProps}>
        <Option value="">{''}</Option>
        {values.map(([key, value] = []) => (
          <Option key={key} value={value}>
            {key}
          </Option>
        ))}
      </InputSelector>
    </FormFieldWrapper>
  );
};

FormSelector.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  values: PropTypes.arrayOf(PropTypes.array).isRequired,
  onChange: PropTypes.func,
  validate: PropTypes.func,
  disabled: PropTypes.bool,
  showErrors: PropTypes.bool,
  defaultValue: PropTypes.string,
};

FormSelector.defaultProps = {
  values: [],
  onBlur: () => {},
  validate: () => true,
  disabled: false,
  showErrors: false,
  defaultValue: '',
};
